import { useEffect, useRef } from 'react';
import { AnimationItem, AnimationConfigWithPath } from 'lottie-web';
import lottie from 'lottie-web/build/player/lottie_light';

const useLazyLottie = (
  options?: Omit<AnimationConfigWithPath, 'container'>,
  stopAnimation = false,
  stopAtFrame = 1,
  handleAnimationComplete?: () => void,
  stopOnLoad = false,
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<AnimationItem | null>(null);

  const onAnimationComplete = () => {
    if (animationRef?.current) {
      animationRef.current.goToAndStop(stopAtFrame, true);
    }
    if (handleAnimationComplete) {
      handleAnimationComplete();
    }
  };

  const onAnimationStop = () => {
    if (animationRef?.current) {
      animationRef.current.stop();
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && options) {
        const animation = lottie?.loadAnimation({
          ...options,
          container: containerRef?.current as Element,
          path: `${window.location.origin}/${options?.path || ''}`,
        });
        animationRef.current = animation;

        // If `stopAnimation` is true, stop once loop is complete
        if (stopAnimation && animationRef?.current) {
          animationRef.current.addEventListener('loopComplete', onAnimationComplete);
        }

        // If `stopOnLoad` is true, stop immediately
        if (stopOnLoad && animationRef?.current) {
          animationRef.current.addEventListener('DOMLoaded', onAnimationStop);
        }

        if (observer?.disconnect) {
          observer.disconnect();
        }
      }
    });

    if (containerRef?.current && observer?.observe) {
      observer.observe(containerRef?.current);
    }

    return () => {
      if (animationRef?.current) {
        animationRef.current.destroy();
      }
      if (observer?.disconnect) {
        observer.disconnect();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, containerRef?.current]);

  return { containerRef, animationRef };
};

export default useLazyLottie;
